import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Award, ClipboardList, FilePenLine, Calendar, Wallet, Gift, TrendingUp, ArrowRight, Sparkles, Clock, Copy, Share2, Mail, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

// Shared dashboard view — used by /admin/dashboard (real data) and /demo (canned data)
export function InstructorDashboardView({ stats, brandName, demo = false, reminders = null }) {
  const money = (n) => Number(n || 0).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const s = stats || {};
  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-12 space-y-8" data-testid={demo ? "demo-dashboard" : "admin-dashboard"}>
      <div>
        <div className="label-caps mb-3 flex items-center gap-2"><Sparkles size={14} /> {brandName || "DyNamic Tax"} · Instructor</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-2">Welcome back{demo ? "" : ""}.</h1>
        <p className="text-muted-foreground">Here's the state of your practice right now — click any card to jump straight to that queue.</p>
      </div>

      {/* Top stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Users size={16} />} label="Students" primary={s.students?.total || 0}
          sub={`${s.students?.active_7d || 0} active this week · ${s.students?.trials_active || 0} on trial`}
          to={demo ? "#" : "/admin"} testid="stat-students"
        />
        <StatCard
          icon={<Wallet size={16} />} label="Revenue booked"
          primary={money(s.payments?.revenue_completed)}
          sub={`${s.payments?.completed || 0} paid · ${s.payments?.pending || 0} pending`}
          to={demo ? "#" : "/admin/payments"} testid="stat-revenue"
        />
        <StatCard
          icon={<ClipboardList size={16} />} label="Intake queue"
          primary={s.intakes?.new || 0}
          sub={`${s.intakes?.reviewed || 0} in progress · ${s.intakes?.completed || 0} filed`}
          to={demo ? "#" : "/admin/intake"} testid="stat-intakes"
        />
        <StatCard
          icon={<Calendar size={16} />} label="Appointments"
          primary={s.appointments?.today || 0}
          sub={`today · ${s.appointments?.upcoming_7d || 0} this week · ${s.appointments?.open || 0} open`}
          to={demo ? "#" : "/admin/appointments"} testid="stat-appointments"
        />
      </div>

      {/* Second row — smaller cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MiniStat icon={<FilePenLine size={14} />} label="Amendments new" v={s.amendments?.new || 0} to={demo ? "#" : "/admin/amendments"} />
        <MiniStat icon={<Award size={14} />} label="AFSP cert earners" v={s.afsp?.certificates || 0} to={demo ? "#" : "/admin/afsp-cohort"} />
        <MiniStat icon={<Gift size={14} />} label="Referral credits out" v={money(s.referrals?.credits_outstanding)} />
        <MiniStat icon={<TrendingUp size={14} />} label="Amendments filed" v={s.amendments?.completed || 0} />
      </div>

      {/* Reminders row — only visible once we have stats from /admin/reminders/summary */}
      {reminders && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniStat icon={<Mail size={14} />} label="Reminders sent today" v={reminders.sent_today || 0}
            sub={`${reminders.sent_24h_today || 0} · 24h  ·  ${reminders.sent_1h_today || 0} · 1h`} />
          <MiniStat icon={<Clock size={14} />} label="Queued next 24h" v={reminders.queued_24h || 0}
            to={demo ? "#" : "/admin/appointments"} />
          <MiniStat icon={<AlertTriangle size={14} />} label="Missing contact"
            v={reminders.missing_contact || 0}
            to={demo ? "#" : "/admin/appointments?filter=missing_contact"}
            warn={reminders.missing_contact > 0} />
          <MiniStat icon={<Calendar size={14} />} label="Reminder cadence" v="24h + 1h" />
        </div>
      )}

      {/* Recent activity */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="p-6 md:col-span-2">
          <div className="label-caps text-[10px] mb-3 flex items-center gap-2"><Clock size={12} /> Recent activity</div>
          {(s.recent_activity || []).length === 0 ? (
            <div className="text-sm text-muted-foreground">Nothing new — as clients submit intakes, book appointments, or request amendments, they'll show up here.</div>
          ) : (
            <ul className="space-y-3" data-testid="recent-activity">
              {(s.recent_activity || []).map((a, i) => (
                <li key={i} className="flex items-start gap-3 border-l-2 border-border pl-3">
                  <span className="label-caps text-[9px] mt-0.5 shrink-0 w-24">{a.kind}</span>
                  <div className="flex-1">
                    <div className="text-sm">{a.label}</div>
                    <div className="text-[10px] text-muted-foreground">{a.when ? new Date(a.when).toLocaleString() : ""}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-6">
          <div className="label-caps text-[10px] mb-3">Quick actions</div>
          <div className="space-y-2">
            <QuickAction to={demo ? "#" : "/admin/appointments"} label="Open new slots" />
            <QuickAction to={demo ? "#" : "/admin/intake"} label="Review new intakes" />
            <QuickAction to={demo ? "#" : "/admin/amendments"} label="Handle amendment requests" />
            <QuickAction to={demo ? "#" : "/admin/payments"} label="Approve pending payments" />
            <QuickAction to={demo ? "#" : "/admin/tenants"} label="White-label tenants" />
            <QuickAction to={demo ? "#" : "/admin/white-label"} label="White-label onboarding queue" />
            <QuickAction to={demo ? "#" : "/admin"} label="Update branding" />
          </div>
        </Card>
      </div>

      {/* Shareable links — copy & send to prospects */}
      {!demo && <ShareLinksCard />}
    </div>
  );
}

const SHARE_LINKS = [
  { key: "white-label",   label: "White-Label System",        sub: "Send to tax pros who want their own branded platform", path: "/white-label",       icon: <Sparkles size={14} /> },
  { key: "affiliate",     label: "Software Affiliate Contract",sub: "Preparers who want to join under your EFIN — sign, upload, submit", path: "/affiliate/apply",  icon: <Users size={14} /> },
  { key: "clients",       label: "For Clients",               sub: "Send to anyone who needs their taxes filed",           path: "/clients",           icon: <ClipboardList size={14} /> },
  { key: "become-tax-pro",label: "Become a Tax Pro",          sub: "Aspiring preparers — enroll in the 1040 course",       path: "/become-a-tax-pro",  icon: <TrendingUp size={14} /> },
  { key: "tax-pros",      label: "For Tax Pros (all offers)", sub: "1120 refresher + AFSP prep + affiliate + white-label", path: "/tax-pros",          icon: <Award size={14} /> },
  { key: "book",          label: "Book an Appointment",       sub: "Direct booking link for clients",                       path: "/book",              icon: <Calendar size={14} /> },
];

function ShareLinksCard() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const copy = (url, label) => {
    navigator.clipboard.writeText(url);
    toast.success(`${label} link copied`);
  };
  return (
    <Card className="p-6" data-testid="admin-share-links">
      <div className="flex items-center gap-2 mb-1">
        <Share2 size={14} className="text-primary" />
        <div className="label-caps text-[10px]">Shareable links</div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Public URLs anyone can visit — text, email, or DM these to prospects. Copy any link with one tap.
      </p>
      <div className="grid md:grid-cols-2 gap-3">
        {SHARE_LINKS.map(l => {
          const url = `${origin}${l.path}`;
          return (
            <div key={l.key} className="border border-border rounded-md p-3 hover:border-primary transition-colors" data-testid={`share-link-${l.key}`}>
              <div className="flex items-center gap-2 mb-1 text-primary">{l.icon}<div className="font-serif text-sm text-foreground">{l.label}</div></div>
              <div className="text-[11px] text-muted-foreground mb-2 leading-snug">{l.sub}</div>
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0 font-mono text-[10px] px-2 py-1.5 rounded bg-secondary/60 border border-border truncate">{url}</div>
                <Button size="sm" variant="outline" onClick={() => copy(url, l.label)} data-testid={`share-link-${l.key}-copy`}>
                  <Copy size={11} className="mr-1" /> Copy
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

const StatCard = ({ icon, label, primary, sub, to, testid }) => {
  const inner = (
    <>
      <div className="flex items-center gap-2 text-primary mb-3">{icon}<span className="label-caps text-[10px]">{label}</span></div>
      <div className="font-serif text-4xl leading-none mb-2">{primary}</div>
      <div className="text-[11px] text-muted-foreground">{sub}</div>
    </>
  );
  const cls = "border border-border rounded-md p-5 bg-card hover:border-primary transition-colors";
  return to && to !== "#" ? (
    <Link to={to} data-testid={testid} className={`${cls} block`}>{inner}</Link>
  ) : (
    <div data-testid={testid} className={cls}>{inner}</div>
  );
};

const MiniStat = ({ icon, label, v, sub, to, warn = false }) => {
  const inner = (
    <>
      <div className={`flex items-center gap-1 mb-1 ${warn ? "text-destructive" : "text-muted-foreground"}`}>{icon}<span className="label-caps text-[9px]">{label}</span></div>
      <div className={`font-serif text-2xl ${warn ? "text-destructive" : ""}`}>{v}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </>
  );
  const cls = `border rounded-md p-4 bg-card hover:border-primary transition-colors ${warn ? "border-destructive/40" : "border-border"}`;
  return to && to !== "#" ? <Link to={to} className={`${cls} block`}>{inner}</Link> : <div className={cls}>{inner}</div>;
};

const QuickAction = ({ to, label }) => (
  to === "#" ? (
    <div className="flex items-center justify-between text-sm text-muted-foreground py-1">
      <span>{label}</span><ArrowRight size={12} />
    </div>
  ) : (
    <Link to={to} className="flex items-center justify-between text-sm py-1 hover:text-primary">
      <span>{label}</span><ArrowRight size={12} />
    </Link>
  )
);

export default function AdminDashboard() {
  const { branding } = useBranding();
  const [stats, setStats] = useState(null);
  const [reminders, setReminders] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => setStats({}));
    api.get("/admin/reminders/summary").then(r => setReminders(r.data)).catch(() => setReminders(null));
  }, []);
  return (
    <Layout>
      {stats === null ? (
        <div className="max-w-6xl mx-auto p-6 lg:p-12 text-muted-foreground">Loading your dashboard…</div>
      ) : (
        <InstructorDashboardView stats={stats} reminders={reminders} brandName={branding?.brand_name} />
      )}
    </Layout>
  );
}
